import type { PayInCategory, PayOutCategory } from './shifts';

/**
 * Closed-shift corrections (Phase 7A-2B, ADR-027). Mirrors
 * supabase/migrations/20260926003900_shift_corrections.sql: the kinds, where corrected cash may be,
 * the one-active rule, which kinds need a fresh authentication, and the arithmetic of the adjusted
 * figures (the originals plus the active corrections). The server decides; these let the pages
 * offer only what it would accept and explain what a correction will do before it is recorded.
 */

/* ── kinds ── */
export const CORRECTION_KINDS = [
  'count',
  'opening',
  'method',
  'pay_in',
  'pay_out',
  'settlement',
  'note',
  'payment_missed',
  'refund_missed',
] as const;
export type CorrectionKind = (typeof CORRECTION_KINDS)[number];
export type CorrectionRowKind = CorrectionKind | 'undo';
export const CORRECTION_KIND_LABELS: Record<CorrectionRowKind, string> = {
  count: 'Close count',
  opening: 'Opening count',
  method: 'Payment method',
  pay_in: 'Pay-in',
  pay_out: 'Pay-out',
  settlement: 'Repayment or write-off',
  note: 'Note',
  payment_missed: 'Missed payment',
  refund_missed: 'Missed refund',
  undo: 'Undo',
};

/** The two kinds that are real ledger rows (Phase 7A-2B.1, ADR-028): recorded by their own commands,
 * withdrawn through the ledger (a void, a cancellation) by the undo, never superseded. */
export const OWNER_ENTRY_KINDS = ['payment_missed', 'refund_missed'] as const;
export type OwnerEntryKind = (typeof OWNER_ENTRY_KINDS)[number];
export function isOwnerEntryKind(kind: CorrectionRowKind): kind is OwnerEntryKind {
  return (OWNER_ENTRY_KINDS as readonly string[]).includes(kind);
}

/** One active correction per kind and row: a new one supersedes the previous. The rest accumulate. */
export const ONE_ACTIVE_KINDS = ['count', 'opening', 'method', 'pay_in', 'pay_out'] as const;
export function supersedes(kind: CorrectionRowKind): boolean {
  return (ONE_ACTIVE_KINDS as readonly string[]).includes(kind);
}

/** Every correction but a note is a sensitive financial action: a recent authentication is required. */
export function correctionNeedsRecentAuth(kind: CorrectionRowKind): boolean {
  return kind !== 'note';
}

/* ── where corrected cash is ── */
export const CASH_LOCATIONS = ['owner', 'next_shift', 'nowhere', 'open_drawer'] as const;
export type CashLocation = (typeof CASH_LOCATIONS)[number];
export const CASH_LOCATION_LABELS: Record<CashLocation, string> = {
  owner: 'With the owner',
  next_shift: 'In the drawer: it opened the next shift',
  nowhere: 'Nowhere: it was never there',
  open_drawer: 'Found later and paid into an open drawer',
};

/**
 * The server's rule on where the cash of a count correction may be, against the figures as
 * recorded at the close. More cash than counted is with the owner, stayed in the drawer or was
 * found later; less was never there (never below what was removed: the drawer cannot have kept less
 * than nothing), or the owner received less than recorded (never less than nothing). A count below
 * both has no single place: nothing is offered.
 */
export function cashLocationsFor(
  deltaMinor: number,
  removedMinor: number,
  retainedMinor: number,
): CashLocation[] {
  if (deltaMinor > 0) return ['owner', 'next_shift', 'open_drawer'];
  if (deltaMinor === 0) return [];
  const out: CashLocation[] = [];
  if (removedMinor + deltaMinor >= 0) out.push('owner');
  if (retainedMinor + deltaMinor >= 0) out.push('nowhere');
  return out;
}

/* ── settlements ── */
export const SETTLEMENT_MODES = ['repaid', 'written_off'] as const;
export type SettlementMode = (typeof SETTLEMENT_MODES)[number];
export const SETTLEMENT_MODE_LABELS: Record<SettlementMode, string> = {
  repaid: 'Repaid',
  written_off: 'Written off',
};
export const SETTLED_TO = ['owner', 'drawer'] as const;
export type SettledTo = (typeof SETTLED_TO)[number];
export const SETTLED_TO_LABELS: Record<SettledTo, string> = {
  owner: 'Handed to the owner',
  drawer: 'Paid into an open drawer',
};

/* ── the adjusted figures (mirrors app.shift_adjusted) ── */
export interface ShiftOriginal {
  openingCountMinor: number;
  /** Null on the first shift of a drawer. */
  expectedOpeningMinor: number | null;
  countedMinor: number;
  /** The expected cash from the rows as they stand (late entries included), before corrections. */
  expectedMinor: number;
  retainedMinor: number;
  removedMinor: number;
  removedTo: 'owner' | 'safe' | null;
  /** A takeover's opening count was the previous close count, not a count of its own. */
  takeover?: boolean;
}

export type ActiveCorrection =
  | { kind: 'count'; countedMinor: number; cashLocation: CashLocation }
  | { kind: 'opening'; countedMinor: number }
  /** A method, pay-in or pay-out correction: what it does to the expected cash. */
  | { kind: 'expected'; deltaMinor: number }
  | { kind: 'settlement'; amountMinor: number };

export interface AdjustedShift {
  openingCountMinor: number;
  expectedOpeningMinor: number | null;
  openingDifferenceMinor: number | null;
  countedMinor: number;
  expectedMinor: number;
  overShortMinor: number;
  retainedMinor: number;
  removedMinor: number;
  removedTo: 'owner' | 'safe' | null;
  settledMinor: number;
  unsettledMinor: number;
  overSettledMinor: number;
}

/** What a count correction moves the next shift's expected opening by: the cash that stayed in the
 * drawer, or was never there. Cash with the owner or found later moves nothing forward. */
export function retainedDeltaMinor(o: ShiftOriginal, corrections: ActiveCorrection[]): number {
  const c = lastCount(corrections);
  if (!c) return 0;
  const delta = c.countedMinor - o.countedMinor;
  return c.cashLocation === 'next_shift' || c.cashLocation === 'nowhere' ? delta : 0;
}

function lastCount(corrections: ActiveCorrection[]) {
  return [...corrections].reverse().find((c) => c.kind === 'count');
}

/**
 * The originals plus the active corrections. `previousRetainedDeltaMinor` is retainedDeltaMinor of
 * the previous shift on the drawer: it moves this shift's expected opening (and, after a takeover,
 * its opening count).
 */
export function adjustShift(
  o: ShiftOriginal,
  corrections: ActiveCorrection[],
  previousRetainedDeltaMinor = 0,
): AdjustedShift {
  const count = lastCount(corrections);
  const opening = [...corrections].reverse().find((c) => c.kind === 'opening');
  const expectedDelta = corrections.reduce(
    (sum, c) => sum + (c.kind === 'expected' ? c.deltaMinor : 0),
    0,
  );
  const settledMinor = corrections.reduce(
    (sum, c) => sum + (c.kind === 'settlement' ? c.amountMinor : 0),
    0,
  );
  const openingCountMinor =
    opening?.countedMinor ?? o.openingCountMinor + (o.takeover ? previousRetainedDeltaMinor : 0);
  const expectedOpeningMinor =
    o.expectedOpeningMinor === null ? null : o.expectedOpeningMinor + previousRetainedDeltaMinor;
  const expectedMinor = o.expectedMinor - o.openingCountMinor + openingCountMinor + expectedDelta;
  const countedMinor = count?.countedMinor ?? o.countedMinor;
  const delta = countedMinor - o.countedMinor;
  const overShortMinor = countedMinor - expectedMinor;
  const retainedMinor =
    o.retainedMinor +
    (count && (count.cashLocation === 'next_shift' || count.cashLocation === 'nowhere')
      ? delta
      : 0);
  const removedMinor = o.removedMinor + (count?.cashLocation === 'owner' ? delta : 0);
  const unsettledMinor = overShortMinor < 0 ? Math.max(0, -overShortMinor - settledMinor) : 0;
  const overSettledMinor =
    overShortMinor < 0 ? Math.max(0, settledMinor + overShortMinor) : settledMinor;
  return {
    openingCountMinor,
    expectedOpeningMinor,
    openingDifferenceMinor:
      expectedOpeningMinor === null ? null : openingCountMinor - expectedOpeningMinor,
    countedMinor,
    expectedMinor,
    overShortMinor,
    retainedMinor,
    removedMinor,
    removedTo: removedMinor > 0 ? (o.removedTo ?? 'owner') : null,
    settledMinor,
    unsettledMinor,
    overSettledMinor,
  };
}

/** The sum of every difference on a drawer: moving cash between places never changes it; only a real
 * correction (a method, an entry, cash that was never there or found later) does. */
export function totalDifferenceMinor(shifts: AdjustedShift[]): number {
  return shifts.reduce((sum, s) => sum + (s.openingDifferenceMinor ?? 0) + s.overShortMinor, 0);
}

/* ── command payloads ── */
export interface CorrectBase {
  shift_id: string;
  reason: string;
  /** The active correction on the row as shown, or null for none: the server refuses when it changed. */
  replaces_id?: string | null;
}
export type ShiftCorrectPayload = CorrectBase &
  (
    | {
        kind: 'count';
        counted_minor: number;
        cash_location: CashLocation;
        linked_movement_id?: string;
      }
    | { kind: 'opening'; counted_minor: number }
    | {
        kind: 'method';
        payment_id?: string;
        reversal_id?: string;
        payment_method_id: string;
        reference?: string;
      }
    | { kind: 'pay_in'; movement_id: string; amount_minor?: number; category?: PayInCategory }
    | {
        kind: 'pay_out';
        movement_id: string;
        amount_minor?: number;
        category?: PayOutCategory;
        receipt_ref?: string;
      }
    | {
        kind: 'settlement';
        amount_minor: number;
        settlement_mode: SettlementMode;
        bearer_employee_id?: string;
        settled_to?: SettledTo;
        linked_movement_id?: string;
        /** A non-cash exception row (not found, or a different amount) instead of the shift's shortage. */
        payment_id?: string;
      }
    | { kind: 'note' }
  );

/* ── money missed on a closed shift (Phase 7A-2B.1, ADR-028): real ledger rows ── */
/** A payment the customer made during the shift that nobody keyed: a real payment row on the closed
 * shift, through the ledger's own capture (the balance rule, the method rules), at the stated time. */
export interface ShiftPaymentMissedPayload {
  shift_id: string;
  /** An open order of the shift's location, placed before the close, still owed money. */
  order_id: string;
  version: number;
  payment_method_id: string;
  amount_minor: number;
  reference?: string;
  /** Within the shift; the close when omitted. */
  occurred_at?: string;
  /** Below the balance: a deposit, by the ledger's partial-payment rule. */
  partial?: boolean;
  /** The same order, method and amount is already recorded on the shift: record it once more. */
  again?: boolean;
  reason: string;
}
/** Money handed back during the shift that nobody keyed: a real refund row on the closed shift from one
 * tender of the order (taken before the close), through the ledger's own refund. Money only: no lines. */
export interface ShiftRefundMissedPayload {
  shift_id: string;
  order_id: string;
  version: number;
  payment_id: string;
  amount_minor: number;
  /** Back by another method than the tender's: recorded as a method override. */
  payment_method_id?: string;
  /** Within the shift; the close when omitted. */
  occurred_at?: string;
  /** The same tender, method and amount is already recorded on the shift: record it once more. */
  again?: boolean;
  reason: string;
}

/** Whether a stated time lies within a closed shift (mirrors app.owner_entry_at). */
export function withinShift(
  shift: { opened_at: string; closed_at: string | null },
  at: string,
): boolean {
  if (shift.closed_at === null) return false;
  const t = Date.parse(at);
  return Number.isFinite(t) && t >= Date.parse(shift.opened_at) && t <= Date.parse(shift.closed_at);
}

/** What an owner entry does to the closed shift's expected cash (D1): a cash payment adds its amount,
 * cash given back takes it away, a non-cash entry moves no cash. Feed it to adjustShift as an
 * `expected` correction to preview the figures. */
export function ownerEntryDeltaMinor(
  kind: OwnerEntryKind,
  methodKind: string,
  amountMinor: number,
): number {
  if (methodKind !== 'cash') return 0;
  return kind === 'payment_missed' ? amountMinor : -amountMinor;
}

export interface UnpaidOrderView {
  id: string;
  order_number: string;
  version: number;
  type: string;
  total_minor: number;
  paid_minor: number;
  refunded_minor: number;
  remaining_minor: number;
  created_at: string;
  customer_name: string | null;
}

/* ── what the pages read ── */
export type CorrectionStatus = 'active' | 'superseded' | 'undone' | 'undo';

export interface ShiftCorrectionView {
  id: string;
  kind: CorrectionRowKind;
  shift_id: string;
  status: CorrectionStatus;
  payment_id: string | null;
  reversal_id: string | null;
  movement_id: string | null;
  counted_minor: number | null;
  cash_location: CashLocation | null;
  linked_movement_id: string | null;
  payment_method: { id: string; name: string; kind: string } | null;
  reference_last4: string | null;
  amount_minor: number | null;
  category: string | null;
  receipt_ref: string | null;
  settlement_mode: SettlementMode | null;
  settled_to: SettledTo | null;
  bearer: { id: string; name: string } | null;
  reason: string;
  facts_before: Record<string, unknown>;
  facts_after: Record<string, unknown>;
  /** The order a ledger entry (or a method correction) belongs to. */
  order: { id: string; order_number: string } | null;
  /** The cancellation an undo of a missed refund recorded. */
  cancel_id: string | null;
  supersedes_id: string | null;
  undoes_id: string | null;
  superseded_by_id: string | null;
  undone_by_id: string | null;
  by: { id: string; name: string };
  at: string;
}

export interface AdjustedFigures {
  opening_count_minor: number;
  expected_opening_minor: number | null;
  opening_difference_minor: number | null;
  counted_minor: number | null;
  expected_minor: number;
  over_short_minor: number | null;
  retained_minor: number | null;
  removed_minor: number | null;
  removed_to: 'owner' | 'safe' | null;
  settled_minor: number;
  unsettled_minor: number;
  over_settled_minor: number;
  corrections: number;
  corrected: boolean;
  count_correction_id: string | null;
  opening_correction_id: string | null;
}

export interface MethodRef {
  id: string;
  name: string;
  kind: string;
}

export interface ShiftPaymentView {
  id: string;
  order_id: string;
  order_number: string;
  amount_minor: number;
  recorded_at: string;
  late_entry: boolean;
  /** Recorded by the owner after the close (ADR-028). */
  owner_entry: boolean;
  /** A void names it: the money is as if never taken. */
  voided: boolean;
  /** What the tender can still give back. */
  reversible_minor: number;
  reference_last4: string | null;
  /** The effective method. */
  method: MethodRef;
  /** The method as recorded, when corrected. */
  recorded_method: MethodRef | null;
  correction_id: string | null;
  received_by: { id: string; name: string } | null;
}

export interface ReversalCancelView {
  id: string;
  reason: string;
  at: string;
  correction_id: string;
  by: { id: string; name: string };
}

export interface ShiftReversalView {
  id: string;
  kind: 'void' | 'refund';
  order_id: string;
  order_number: string;
  payment_id: string | null;
  amount_minor: number;
  recorded_at: string;
  method: MethodRef;
  recorded_method: MethodRef | null;
  correction_id: string | null;
  method_override: boolean;
  /** Recorded by the owner after the close (ADR-028). */
  owner_entry: boolean;
  /** Cancelled: the refund stays in the history and in no money figure. */
  cancelled: boolean;
  cancel: ReversalCancelView | null;
}

/** A pay-in on a later shift that a count correction or a repayment may name, once. */
export interface LinkablePayIn {
  id: string;
  amount_minor: number;
  reason: string | null;
  category: string | null;
  recorded_at: string;
  shift_id: string;
  shift_seq: number;
  by: { id: string; name: string };
}

/** The effective amount, category, receipt and flags of a corrected pay-in or pay-out. */
/** A pay-in or pay-out as it reads now: corrected, or (a pay-out) with its day limits re-run because another pay-out of the day was. */
export interface MovementEffectiveView {
  amount_minor: number;
  category: string | null;
  receipt_ref: string | null;
  flags: Record<string, unknown>;
  correction_id: string | null;
}

/** Refusal hints the correction commands attach (`hint.review`). */
export type CorrectionRefusal =
  | 'not_closed'
  | 'changed'
  | 'no_change'
  | 'removed_to_safe'
  | 'takeover_opening'
  | 'voided'
  | 'complimentary'
  | 'not_short'
  | 'settled'
  | 'not_exception'
  | 'is_undo'
  | 'undone'
  | 'superseded'
  | 'link_not_pay_in'
  | 'link_voided'
  | 'link_before_close'
  | 'link_amount'
  | 'link_taken'
  | 'order_after_close'
  | 'tender_after_close'
  | 'duplicate'
  | 'refunded'
  | 'not_owner_entry';
